import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import { Grid, Box, CircularProgress } from "@mui/material";

import { DateTime } from "luxon";

import useApi from "../../../../../../hooks/useApi";
import { constant_names } from "../../../../../../utils/constants";
import { generateAvailableDays } from "../../../../../../utils/helpers";
import LogoHeader from "../../../../../UI/LogoHeader";
import DateTimeOptions from "./DateTimeOptions";
import CalendarButton from "./CalendarButton";

//Generate the calendar buttons separated by week day from the generated days
const generateCalendarButtons = (selectedMonth, availableDays, branchTimeZone) => {
  const firstDayWeekday = selectedMonth.startOf("month").weekday;
  const calendarWeekdays = { Sun: [], Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [] };

  if (firstDayWeekday !== 7) {
    for (let i = 1; i <= firstDayWeekday; i++) {
      calendarWeekdays[constant_names.WEEKDAYS_NAMES[i]].push(0);
    }
  }

  Object.keys(availableDays).forEach((day) => {
    const dayObject = DateTime.fromISO(day, { zone: branchTimeZone });

    if (calendarWeekdays[dayObject.weekdayShort]) {
      calendarWeekdays[dayObject.weekdayShort].push({
        dayNumber: dayObject.day,
        dayIso: day,
        disabled: availableDays[day] === null,
      });
    }
  });

  return calendarWeekdays;
};

export default function Calendar(props) {
  const {
    onSetDateInfo,
    initialMonth,
    selectedBranch,
    branchInfo,
    branchDayStart,
    minLeadDayIso,
    maxLeadDayIso,
    selectedDateInfo,
    availableMonths,
  } = props;

  const globalLoading = useSelector((state) => state.ui.loading);

  const [selectedMonth, setSelectedMonth] = useState(
    DateTime.fromObject({ year: initialMonth.year, month: initialMonth.month })
  );
  const [blockedTimes, setBlockedTimes] = useState();
  const [meetings, setMeetings] = useState();

  const { isLoading: loadingBlockedTimes, sendRequest: getBlockedTimes } = useApi({
    initialLoading: true,
    successMessage: "Blocked times loaded successfully",
    errorMessage: "Error loading blocked times",
  });
  const { isLoading: loadingMeetings, sendRequest: getMeetings } = useApi({
    initialLoading: true,
    successMessage: "Meetings loaded successfully",
    errorMessage: "Error loading meetings",
  });

  const loading = !availableMonths || loadingBlockedTimes || loadingMeetings;

  const availableDays = useMemo(
    () => generateAvailableDays(loading, availableMonths, branchInfo, meetings, blockedTimes),
    [availableMonths, blockedTimes, branchInfo, loading, meetings]
  );

  const monthAvailableDays = availableDays && availableDays[selectedMonth.month];
  const branchTimeZone = branchInfo.timeZone;

  const calendarWeekdays =
    selectedMonth &&
    branchTimeZone &&
    availableDays &&
    generateCalendarButtons(selectedMonth, monthAvailableDays, branchTimeZone);

  const selectedMonthChangeHandler = (monthObject) => {
    setSelectedMonth(monthObject);
  };

  const dayChangeHandler = (event) => {
    const dateInfo = monthAvailableDays[event.target.value];
    const monthDaysKeys = Object.keys(monthAvailableDays);
    const todayIso = DateTime.now()
      .set({ hour: branchDayStart.hour, minute: branchDayStart.minute, second: 0, millisecond: 0 })
      .toISO();

    //Check next day available day (not null)
    for (let i = monthDaysKeys.indexOf(todayIso) + 1; i < monthDaysKeys.length; i++) {
      const value = monthAvailableDays[monthDaysKeys[i]];

      if (value) {
        dateInfo.nextOpenDate = value.date;
        break;
      }
    }

    //If still couldn't find an open day in this month (end of month), check for an available date in next month
    if (!dateInfo.nextOpenDate) {
      const nextMonthAvailableDays = availableDays[selectedMonth.month];
      const nextMonthDaysKeys = Object.keys(nextMonthAvailableDays);

      for (let i = 1; i < nextMonthDaysKeys.length; i++) {
        const value = monthAvailableDays[nextMonthDaysKeys[i]];

        if (value) {
          dateInfo.nextOpenDate = value.date;
          break;
        }
      }
    }

    onSetDateInfo(dateInfo);
  };

  const blockedTimesCallback = useCallback((data) => {
    const blockedTimes = data.blockedTimes;
    setBlockedTimes(blockedTimes);
  }, []);

  const meetingsCallback = useCallback((data) => {
    const meetings = data.meetings;
    setMeetings(meetings);
  }, []);

  useEffect(() => {
    const payload = { selectedBranch, dayStart: minLeadDayIso, dayEnd: maxLeadDayIso };

    getBlockedTimes(
      {
        url: "api/booking-app/blocked-times.php",
        payload,
      },
      blockedTimesCallback
    );

    getMeetings(
      {
        url: "api/booking-app/meetings.php",
        payload,
      },
      meetingsCallback
    );
  }, [
    blockedTimesCallback,
    getBlockedTimes,
    getMeetings,
    maxLeadDayIso,
    meetingsCallback,
    minLeadDayIso,
    selectedBranch,
  ]);

  return loading || !calendarWeekdays ? (
    <Grid container justifyContent="center" alignItems="center" height="300px">
      <CircularProgress />
    </Grid>
  ) : (
    <Grid container direction="column" width="350px" spacing={1}>
      <Grid item>
        <LogoHeader title="Select a date" />
      </Grid>

      <Grid item>
        <DateTimeOptions
          onMonthChange={selectedMonthChangeHandler}
          selectedMonth={selectedMonth}
          availableMonths={availableMonths}
        />
      </Grid>

      <Grid item>
        <Grid container justifyContent="space-between">
          {Object.keys(calendarWeekdays).map((weekDay) => (
            <Grid item key={weekDay}>
              <Grid container direction="column" alignItems="center" spacing={2}>
                <Grid item>{weekDay}</Grid>
                {calendarWeekdays[weekDay].map((day, i) => {
                  return (
                    <Grid item key={i}>
                      {day === 0 ? (
                        <Box minHeight={40}></Box>
                      ) : (
                        <CalendarButton
                          day={day}
                          onChangeDay={dayChangeHandler}
                          selectedDateInfo={selectedDateInfo}
                          globalLoading={globalLoading}
                        />
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
}
